import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Avatar,
  Divider,
  Slide,
  Backdrop,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Close,
  Send,
  Comment,
  Reply,
  ThumbUp,
  ThumbDown,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import CommenterRegistrationForm from './CommenterRegistrationForm';
import CommenterSignInForm from './CommenterSignInForm';
import Cookies from 'js-cookie';
import { config } from '../config';

const CommentBottomSheet = ({ open, onClose, blogPostId, blogTitle }) => {
  const { user: portalUser, registerCommenter } = useAuth();
  const [commentingUser, setCommentingUser] = useState(null); // Local state for commenter authentication
  const [authLoading, setAuthLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showRegistration, setShowRegistration] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [authMode, setAuthMode] = useState('none'); // 'none', 'signin', 'register'

  // Use portal user if available, otherwise use local commenting user
  const user = portalUser || commentingUser;

  // Reply functionality state
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  // Expand/collapse state
  const [expandedComments, setExpandedComments] = useState(new Set());


  // Check user authentication when sheet opens
  const checkUserAuthentication = async () => {
    setAuthLoading(true);
    try {
      const token = Cookies.get('auth_token');
      if (token) {
        // Check if user exists and is valid
        const userData = await apiService.getCurrentUser();
        console.log('User authentication check result:', userData);

        // Handle commenter users separately (local state only, not portal authentication)
        if (userData.role === 'commenter') {
          console.log('Commenter user authenticated for commenting only');
          setCommentingUser(userData);
        } else if (userData.role === 'affiliate') {
          console.log('Affiliate user authenticated');
          // Affiliate users are managed by AuthContext
        } else {
          console.log('User role not allowed:', userData.role);
          Cookies.remove('auth_token');
        }
      } else {
        console.log('No auth token found');
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      Cookies.remove('auth_token');
    } finally {
      setAuthLoading(false);
    }
  };

  // Load comments and check authentication when sheet opens
  useEffect(() => {
    if (open && blogPostId) {
      console.log('Comment sheet opened - checking authentication and loading comments');
      checkUserAuthentication();
      loadComments();
    } else if (open && !blogPostId) {
      console.log('No blog post ID provided for comments - this might be a new blog post');
      setError('Comments are only available for published blog posts');
    }
  }, [open, blogPostId]);

  const loadComments = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching comments from API for post ID:', blogPostId);
      const commentsData = await apiService.getComments(blogPostId);
      console.log('Comments data received:', commentsData);
      setComments(commentsData);
    } catch (err) {
      console.error('Error loading comments:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !user) return;

    setSubmitting(true);
    setError('');
    try {
      await apiService.addComment(blogPostId, newComment.trim());
      setNewComment('');
      // Reload all comments to ensure we display all comments, not just the user's own
      await loadComments();
    } catch (err) {
      console.error('Error submitting comment:', err);
      setError('Failed to submit comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegistrationSuccess = async (userData) => {
    try {
      console.log('Registration successful, updating user state:', userData);

      // Handle commenter users separately (local state only, not portal authentication)
      if (userData.role === 'commenter') {
        setCommentingUser(userData);
        setAuthMode('none');
        setShowRegistration(false);
      } else if (userData.role === 'affiliate') {
        // Affiliate users can use portal authentication
        await registerCommenter(userData);
        setAuthMode('none');
        setShowRegistration(false);
      }

      // Reload comments after successful registration
      if (blogPostId) {
        loadComments();
      }
    } catch (error) {
      console.error('Error registering user:', error);
      setError('Registration successful but failed to update session. Please refresh the page.');
    }
  };

  const handleSignInSuccess = async (userData) => {
    try {
      console.log('Sign in successful, updating user state:', userData);

      // Handle commenter users separately (local state only, not portal authentication)
      if (userData.role === 'commenter') {
        setCommentingUser(userData);
        setAuthMode('none');
        setShowSignIn(false);
      } else if (userData.role === 'affiliate') {
        // Affiliate users can use portal authentication
        await registerCommenter(userData);
        setAuthMode('none');
        setShowSignIn(false);
      }

      // Reload comments after successful sign in
      if (blogPostId) {
        loadComments();
      }
    } catch (error) {
      console.error('Error updating session after sign in:', error);
      setError('Sign in successful but failed to update session. Please refresh the page.');
    }
  };

  const handleShowRegistration = () => {
    setAuthMode('register');
    setShowRegistration(true);
    setShowSignIn(false);
    setError('');
  };

  const handleShowSignIn = () => {
    setAuthMode('signin');
    setShowSignIn(true);
    setShowRegistration(false);
    setError('');
  };

  const handleCancelRegistration = () => {
    setAuthMode('none');
    setShowRegistration(false);
    setError('');
  };

  const handleCancelSignIn = () => {
    setAuthMode('none');
    setShowSignIn(false);
    setError('');
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmitComment();
    }
  };

  // Reply functionality
  const handleReplyClick = (commentId) => {
    setReplyingTo(commentId);
    setReplyText('');
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim() || !replyingTo) return;

    setSubmittingReply(true);
    setError('');
    try {
      await apiService.addReply(blogPostId, replyingTo, replyText.trim());
      setReplyText('');
      setReplyingTo(null);
      // Reload all comments to show the new reply
      await loadComments();
    } catch (err) {
      console.error('Error submitting reply:', err);
      setError('Failed to submit reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  // Like/Dislike functionality
  const handleLikeComment = async (commentId) => {
    if (!user) return;

    try {
      await apiService.likeComment(blogPostId, commentId);
      // Reload comments to update like counts
      await loadComments();
    } catch (err) {
      console.error('Error liking comment:', err);
      setError('Failed to like comment');
    }
  };

  const handleDislikeComment = async (commentId) => {
    if (!user) return;

    try {
      await apiService.dislikeComment(blogPostId, commentId);
      // Reload comments to update dislike counts
      await loadComments();
    } catch (err) {
      console.error('Error disliking comment:', err);
      setError('Failed to dislike comment');
    }
  };

  const handleRemoveReaction = async (commentId) => {
    if (!user) return;

    try {
      await apiService.removeReaction(blogPostId, commentId);
      // Reload comments to update reaction counts
      await loadComments();
    } catch (err) {
      console.error('Error removing reaction:', err);
      setError('Failed to remove reaction');
    }
  };

  const isCommentLiked = (comment) => {
    return comment.likes && comment.likes.some(like => like.user.toString() === user?.id);
  };

  const isCommentDisliked = (comment) => {
    return comment.dislikes && comment.dislikes.some(dislike => dislike.user.toString() === user?.id);
  };

  // Organize comments into nested structure
  const organizeComments = (comments) => {
    const commentMap = new Map();
    const rootComments = [];

    // First pass: create map of all comments
    comments.forEach(comment => {
      commentMap.set(comment._id, { ...comment, replies: [] });
    });

    // Second pass: organize into hierarchy
    comments.forEach(comment => {
      if (comment.parentComment) {
        const parent = commentMap.get(comment.parentComment);
        if (parent) {
          parent.replies.push(commentMap.get(comment._id));
        }
      } else {
        rootComments.push(commentMap.get(comment._id));
      }
    });

    return rootComments;
  };

  // Toggle expand/collapse for a comment
  const toggleCommentExpansion = (commentId) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(commentId)) {
      newExpanded.delete(commentId);
    } else {
      newExpanded.add(commentId);
    }
    setExpandedComments(newExpanded);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) { // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Recursive component to render nested comments
  const renderComment = (comment, depth = 0) => {
    const hasReplies = comment.replies && comment.replies.length > 0;
    const isExpanded = expandedComments.has(comment._id);
    const isReply = depth > 0;

    return (
      <Box key={comment._id} sx={{ mb: depth === 0 ? 2 : 1 }}>
        <Box sx={{
          display: 'flex',
          gap: 2,
          ml: isReply ? 2 : 0,
          pl: isReply ? 1 : 0,
          position: 'relative'
        }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: '#1976d2',
              fontSize: '14px'
            }}
            src={comment.user?.profileIcon?.path ? `${config.SERVER_URL}${comment.user.profileIcon.path}` : undefined}
          >
            {comment.user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {comment.user?.name || 'Anonymous'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                {formatDate(comment.createdAt)}
              </Typography>
              {isReply && (
                <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                  replied
                </Typography>
              )}
            </Box>
            <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.4 }}>
              {comment.text}
            </Typography>

            {/* Action buttons */}
            {user && (
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                  <Button
                    size="small"
                    startIcon={<Reply sx={{ fontSize: 16 }} />}
                    onClick={() => handleReplyClick(comment._id)}
                    sx={{
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      fontSize: '12px',
                      textTransform: 'none',
                      color: '#666',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    Reply
                  </Button>

                  <Button
                    size="small"
                    startIcon={<ThumbUp sx={{ fontSize: 16 }} />}
                    onClick={() => isCommentLiked(comment) ? handleRemoveReaction(comment._id) : handleLikeComment(comment._id)}
                    sx={{
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      fontSize: '12px',
                      textTransform: 'none',
                      color: isCommentLiked(comment) ? '#1976d2' : '#666',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    {comment.likes?.length || 0}
                  </Button>

                  <Button
                    size="small"
                    startIcon={<ThumbDown sx={{ fontSize: 16 }} />}
                    onClick={() => isCommentDisliked(comment) ? handleRemoveReaction(comment._id) : handleDislikeComment(comment._id)}
                    sx={{
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      fontSize: '12px',
                      textTransform: 'none',
                      color: isCommentDisliked(comment) ? '#d32f2f' : '#666',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    {comment.dislikes?.length || 0}
                  </Button>
                </Box>

                {/* Expand/Collapse button for comments with replies - on new row */}
                {hasReplies && (
                  <Box sx={{ mt: 0.5 }}>
                    <Button
                      size="small"
                      startIcon={isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                      onClick={() => toggleCommentExpansion(comment._id)}
                      sx={{
                        minWidth: 'auto',
                        px: 1,
                        py: 0.5,
                        fontSize: '12px',
                        textTransform: 'none',
                        color: '#666',
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                    >
                      {isExpanded ? 'Hide replies' : `View ${comment.replies.length} ${comment.replies.length === 1 ? 'Reply' : 'Replies'}`}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Reply input */}
            {replyingTo === comment._id && (
              <Box sx={{ mt: 2, ml: 1, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Write a reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  disabled={submittingReply}
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px'
                    }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    onClick={handleCancelReply}
                    disabled={submittingReply}
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSubmitReply}
                    disabled={!replyText.trim() || submittingReply}
                    sx={{ textTransform: 'none' }}
                  >
                    {submittingReply ? <CircularProgress size={16} /> : 'Reply'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Render replies */}
            {hasReplies && isExpanded && (
              <Box sx={{
                mt: 2,
                ml: 2,
                pl: 2,
                borderLeft: '2px solid #e0e0e0',
                backgroundColor: '#fafafa',
                borderRadius: 1,
                py: 1
              }}>
                <Typography variant="caption" sx={{
                  color: '#666',
                  fontWeight: 600,
                  mb: 1,
                  display: 'block'
                }}>
                  Replies ({comment.replies.length})
                </Typography>
                {comment.replies.map(reply => renderComment(reply, depth + 1))}
              </Box>
            )}
          </Box>
        </Box>
        {depth === 0 && (
          <Divider sx={{ mt: 2, opacity: 0.3 }} />
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        pointerEvents: open ? 'auto' : 'none',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        '@media (max-width: 768px)': {
          width: '100vw',
          height: '100vh',
          left: 0,
          right: 0,
          margin: 0,
          padding: 0
        }
      }}
    >
      <Backdrop
        open={open}
        onClick={onClose}
        sx={{
          zIndex: 1300,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          width: '100%',
          height: '100%',
          '@media (max-width: 768px)': {
            width: '100vw',
            height: '100vh'
          }
        }}
      />

      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          sx={{
            position: 'relative',
            height: '70vh',
            maxHeight: '600px',
            zIndex: 1400,
            borderRadius: '20px 20px 0 0',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
            width: '375px',
            maxWidth: '375px',
            margin: 0,
            padding: 0,
            '@media (max-width: 768px)': {
              borderRadius: '20px 20px 0 0',
              width: '100vw !important',
              maxWidth: '100vw !important',
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              margin: '0 !important',
              padding: '0 !important',
              boxSizing: 'border-box'
            }
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 64,
              px: 2,
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#f8f9fa',
              position: 'relative'
            }}
          >
            <Box
              component="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1.25rem',
                margin: 0,
                padding: 0,
                display: 'block'
              }}
            >
              {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)'
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Comments List */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              backgroundColor: '#ffffff',
              width: '100%',
              '@media (max-width: 768px)': {
                width: '100%',
                px: 2,
                py: 2
              }
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : comments.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Comment sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#666' }}>
                  {blogPostId ? 'No comments yet' : 'Comments not available'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                  {blogPostId ? 'Be the first to share your thoughts!' : 'Comments are only available for published blog posts'}
                </Typography>
              </Box>
            ) : (
              organizeComments(comments).map(comment => renderComment(comment))
            )}
          </Box>

          {/* Comment Input */}
          {user && blogPostId && (
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid #e0e0e0',
                backgroundColor: '#f8f9fa',
                width: '100%',
                '@media (max-width: 768px)': {
                  width: '100%',
                  px: 2,
                  py: 2
                }
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={submitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '20px',
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim() || submitting}
                  sx={{
                    minWidth: 'auto',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0'
                    },
                    '&:disabled': {
                      backgroundColor: '#ccc'
                    }
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Send sx={{ fontSize: 18 }} />
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {/* Not logged in message */}
          {!authLoading && !user && authMode === 'none' && (
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #e0e0e0',
                width: '100%',
                '@media (max-width: 768px)': {
                  width: '100%',
                  px: 2,
                  py: 2
                }
              }}
            >
              <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                Please sign in or register to add comments
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleShowSignIn}
                  sx={{
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0'
                    }
                  }}
                >
                  Sign In
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleShowRegistration}
                  sx={{
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#1565c0'
                    }
                  }}
                >
                  Register
                </Button>
              </Box>
            </Box>
          )}

          {/* Sign In form */}
          {!authLoading && !user && showSignIn && (
            <Box
              sx={{
                p: 2,
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #e0e0e0',
                width: '100%',
                '@media (max-width: 768px)': {
                  width: '100%',
                  px: 2,
                  py: 2
                }
              }}
            >
              <CommenterSignInForm
                onSuccess={handleSignInSuccess}
                onCancel={handleCancelSignIn}
                onSwitchToRegister={handleShowRegistration}
              />
            </Box>
          )}

          {/* Registration form */}
          {!authLoading && !user && showRegistration && (
            <Box
              sx={{
                p: 2,
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #e0e0e0',
                width: '100%',
                '@media (max-width: 768px)': {
                  width: '100%',
                  px: 2,
                  py: 2
                }
              }}
            >
              <CommenterRegistrationForm
                onSuccess={handleRegistrationSuccess}
                onCancel={handleCancelRegistration}
              />
            </Box>
          )}

          {/* Debug info */}
          {/* {process.env.NODE_ENV === 'development' && (
            <Box sx={{ p: 1, backgroundColor: '#f0f0f0', fontSize: '12px' }}>
              Debug: user={user ? 'exists' : 'null'}, authMode={authMode}, authLoading={authLoading ? 'true' : 'false'}
            </Box>
          )} */}

          {/* Authentication loading */}
          {authLoading && (
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #e0e0e0',
                width: '100%',
                '@media (max-width: 768px)': {
                  width: '100%',
                  px: 2,
                  py: 2
                }
              }}
            >
              <CircularProgress size={24} sx={{ mb: 1 }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                Loading...
              </Typography>
            </Box>
          )}

          {/* No blog post ID message */}
          {user && !blogPostId && (
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #e0e0e0',
                width: '100%',
                '@media (max-width: 768px)': {
                  width: '100%',
                  px: 2,
                  py: 2
                }
              }}
            >
              <Typography variant="body2" sx={{ color: '#666' }}>
                Comments are only available for published blog posts
              </Typography>
            </Box>
          )}
        </Paper>
      </Slide>
    </Box>
  );
};

export default CommentBottomSheet;
